
import React, { useEffect, useState } from "react";
import { useTodosLiveMonitor, LogEvent } from "../../hooks/useTodosLiveMonitor";

export function DashboardTodosLiveDebugTab() {
    const {
        liveStatus, liveMsg, data,
        lastSnapshotAt, lastSnapshotFromCache, snapshotCount,
        lastError, retryAttempt, retryNextAt,
        isListening, hasUnsub, networkOnline,
        probe, eventLog,
        start, stop, reconnect, probeServer
    } = useTodosLiveMonitor();

    // Auto refresh timer for relative time display
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const getStatusColor = (s: string) => {
        if (s === 'connected') return '#10b981'; // Green
        if (s === 'connecting') return '#fbbf24'; // Yellow
        if (s === 'offline') return '#6b7280'; // Gray
        if (s === 'error') return '#ef4444'; // Red
        return '#aaa';
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, height: '100%', paddingBottom: 20 }}>
            {/* A. Status Overview */}
            <div className="section">
                <h3 className="h1" style={{ color: '#60a5fa', marginBottom: 12 }}>A. 即時狀態總覽 (Live Status)</h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
                    <Card label="Live Status" value={liveStatus.toUpperCase()} color={getStatusColor(liveStatus)} big />
                    <Card label="Listener Active" value={isListening ? "YES" : "NO"} color={isListening ? 'var(--ok)' : '#666'} />
                    <Card label="Unsub Function" value={hasUnsub ? "Present" : "Null"} />
                    <Card label="Network (Nav)" value={networkOnline ? "Online" : "Offline"} color={networkOnline ? 'var(--ok)' : 'var(--danger)'} />
                    
                    <Card label="Last Snapshot" value={lastSnapshotAt ? new Date(lastSnapshotAt).toLocaleTimeString() : "-"} />
                    <Card label="From Cache" value={lastSnapshotFromCache === null ? "-" : (lastSnapshotFromCache ? "YES" : "NO")} color={lastSnapshotFromCache ? '#fbbf24' : 'var(--ok)'} />
                    <Card label="Snapshot Count" value={snapshotCount} />
                    
                    <Card label="Retry Attempt" value={retryAttempt} color={retryAttempt > 0 ? '#fbbf24' : '#ccc'} />
                </div>
                
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: 8, borderLeft: `4px solid ${getStatusColor(liveStatus)}` }}>
                    <div style={{fontWeight:'bold', marginBottom:4}}>UI Message:</div>
                    <div style={{fontSize: 16, color: '#fff'}}>{liveMsg}</div>
                    {retryNextAt && retryNextAt > now && (
                        <div style={{marginTop:8, color:'#fbbf24'}}>
                            ⚠️ Next retry in: {Math.ceil((retryNextAt - now)/1000)}s
                        </div>
                    )}
                </div>
            </div>

            {/* B. Actions */}
            <div className="section">
                <h3 className="h1" style={{ color: '#fbbf24', marginBottom: 12 }}>B. 診斷操作 (Actions)</h3>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button className="btn" style={{ width: 'auto', background: '#3b82f6' }} onClick={start} disabled={isListening}>
                        ▶ 啟動監聽 (Start)
                    </button>
                    <button className="btn" style={{ width: 'auto', background: '#333', border: '1px solid #555' }} onClick={stop} disabled={!isListening}>
                        ⏹ 停止監聽 (Stop)
                    </button>
                    <button className="btn" style={{ width: 'auto', background: '#f59e0b', color: '#000' }} onClick={reconnect}>
                        🔄 手動重連 (Reconnect)
                    </button>
                    <button className="btn" style={{ width: 'auto', background: '#8b5cf6' }} onClick={() => probeServer().catch(()=>{})}>
                        ⚡ Probe Server
                    </button>
                </div>
            </div>

            {/* C. Probe Results */}
            <div className="section">
                <h3 className="h1" style={{ color: '#a78bfa', marginBottom: 12 }}>C. Server Probe 結果</h3>
                <div style={{ background: '#111', padding: 12, borderRadius: 8, fontFamily: 'monospace', fontSize: 13, color: '#ccc' }}>
                    {probe.lastProbeAt ? (
                        <>
                            <div>Time: {new Date(probe.lastProbeAt).toLocaleTimeString()}</div>
                            <div style={{ color: probe.ok ? 'var(--ok)' : 'var(--danger)' }}>
                                Result: {probe.ok ? "SUCCESS" : "FAILED"}
                            </div>
                            {probe.errorCode && <div>Code: {probe.errorCode}</div>}
                            {probe.errorMessage && <div>Msg: {probe.errorMessage}</div>}
                        </>
                    ) : (
                        <div>(尚未執行 Probe)</div>
                    )}
                </div>
            </div>

            {/* D. Event Log */}
            <div className="section" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 300 }}>
                <h3 className="h1" style={{ color: '#34d399', marginBottom: 12 }}>D. 事件紀錄 (Event Log)</h3>
                <div style={{ flex: 1, overflowY: 'auto', background: '#000', border: '1px solid #333', borderRadius: 8, padding: 10, fontFamily: 'monospace', fontSize: 12 }}>
                    {eventLog.length === 0 && <div style={{ color: '#666', textAlign: 'center', marginTop: 20 }}>無事件</div>}
                    {eventLog.map((log, idx) => (
                        <div key={idx} style={{ marginBottom: 4, borderBottom: '1px solid #222', paddingBottom: 4 }}>
                            <span style={{ color: '#888', marginRight: 8 }}>[{log.time}]</span>
                            <span style={{ color: getLogTypeColor(log.type), fontWeight: 'bold', marginRight: 8, minWidth: 70, display: 'inline-block' }}>{log.type}</span>
                            <span style={{ color: '#ddd' }}>{log.payload}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Card({ label, value, color, big }: any) {
    return (
        <div style={{ background: '#222', padding: 12, borderRadius: 6, border: '1px solid #333' }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: big ? 18 : 14, fontWeight: 'bold', color: color || '#fff' }}>{value}</div>
        </div>
    );
}

function getLogTypeColor(type: LogEvent['type']) {
    switch (type) {
        case 'START': return '#3b82f6';
        case 'STOP': return '#666';
        case 'SNAPSHOT': return '#10b981';
        case 'ERROR': return '#ef4444';
        case 'RETRY': return '#f59e0b';
        case 'PROBE': return '#a78bfa';
        default: return '#ccc';
    }
}
